import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Copy, Mail, Phone, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [orderRes, settingsRes] = await Promise.all([
          api.get(`/orders/lookup/${orderId}`, { params: { token } }),
          api.get(`/settings`),
        ]);
        setOrder(orderRes.data);
        setSettings(settingsRes.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Could not find this order.");
      } finally {
        setLoading(false);
      }
    })();
  }, [orderId, token]);

  const copy = (val) => { navigator.clipboard.writeText(val); toast.success("Copied to clipboard"); };

  if (loading) {
    return <main className="max-w-3xl mx-auto px-6 py-24 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-[#1A6B2E]" /></main>;
  }
  if (error || !order) {
    return (
      <main data-testid="order-not-found" className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="chop-card chop-shadow p-8 md:p-14 text-center">
          <h1 className="font-display text-3xl mt-3 tracking-tighter">Order not found</h1>
          <p className="text-[#4E5C4F] mt-2">{error || "The link may be broken."}</p>
          <Link to="/menu" className="btn-primary inline-block mt-6 rounded-full px-6 py-3 text-sm font-semibold">Back to menu</Link>
        </div>
      </main>
    );
  }

  const interacEmail = settings?.interac_email || "chopboxco@hotmail.com";
  const referenceMsg = `${order.customer_name}, Ontario · Order ${order.order_id.slice(-6).toUpperCase()}`;

  return (
    <main data-testid="order-confirmation" className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16 space-y-6">
      {/* Success header */}
      <div className="chop-card chop-shadow p-8 md:p-10 text-center">
        <CheckCircle2 className="w-14 h-14 mx-auto text-[#1A6B2E]" />
        <h1 data-testid="confirmation-title" className="font-display text-4xl md:text-5xl mt-4 tracking-tighter">
          Order placed. Now the last step.
        </h1>
        <p className="text-[#4E5C4F] mt-3">
          Order <span className="font-mono text-[#0E2B13]" data-testid="conf-order-id">{order.order_id}</span> is waiting for your Interac e-Transfer.
        </p>
      </div>

      {/* INTERAC INSTRUCTIONS */}
      <div className="chop-card chop-shadow p-6 md:p-8 border-l-4 border-[#D02030]">
        <span className="font-label text-[10px] text-[#D02030]">Payment · Interac e-Transfer</span>
        <h2 className="font-display text-2xl md:text-3xl mt-2">Send your payment to complete the order.</h2>
        <p className="text-sm text-[#4E5C4F] mt-2">
          Your order is confirmed once we receive your Interac e-Transfer for <b className="text-[#0E2B13]">${order.total.toFixed(2)} CAD</b>.
        </p>

        <div className="mt-5 rounded-xl bg-[#F4EFEB]/60 p-5 space-y-3 text-sm">
          <Field
            label="Send e-Transfer to"
            value={interacEmail}
            onCopy={() => copy(interacEmail)}
            testid="conf-interac-email"
            mono
          />
          <Field
            label="Amount"
            value={`$${order.total.toFixed(2)} CAD`}
            onCopy={() => copy(order.total.toFixed(2))}
            testid="conf-interac-amount"
          />
          <Field
            label="Message / reference"
            value={referenceMsg}
            onCopy={() => copy(referenceMsg)}
            testid="conf-interac-message"
            help="Include your full name and province so we can match your payment to this order."
          />
        </div>

        <p className="mt-5 text-xs text-[#4E5C4F]">
          <b className="text-[#0E2B13]">What happens next?</b> We'll email you at{" "}
          <span className="font-mono">{order.customer_email}</span> once the transfer lands. If we don't see it within 24 hours, we'll call{" "}
          <span className="font-mono">{order.customer_phone}</span>.
        </p>
      </div>

      {/* ORDER RECAP */}
      <div className="chop-card chop-shadow p-6 md:p-8">
        <h3 className="font-display text-2xl">Order recap</h3>

        <div className="mt-4 space-y-2 text-sm">
          <p className="flex items-start gap-2"><MapPin className="w-4 h-4 text-[#1A6B2E] mt-0.5 shrink-0" />
            <span><b>{order.city}</b>{order.frozen_only && <span className="ml-2 text-[10px] font-label bg-[#1A6B2E]/10 text-[#1A6B2E] px-2 py-0.5 rounded-full">FROZEN SHIPPING</span>}</span>
          </p>
          <p className="flex items-start gap-2"><Clock className="w-4 h-4 text-[#1A6B2E] mt-0.5 shrink-0" />
            <span>Minimum 72 hours from now. Your preferred date is in the notes below.</span>
          </p>
          <p className="flex items-start gap-2"><Phone className="w-4 h-4 text-[#1A6B2E] mt-0.5 shrink-0" /><span>{order.customer_phone}</span></p>
          <p className="flex items-start gap-2"><Mail className="w-4 h-4 text-[#1A6B2E] mt-0.5 shrink-0" /><span>{order.customer_email}</span></p>
        </div>

        <div className="mt-5 pt-5 border-t border-[#1A6B2E]/10">
          <p className="font-label text-[10px] text-[#4E5C4F] mb-2">Items</p>
          <div className="space-y-2">
            {(order.items || []).map((it, i) => (
              <div key={i} className="flex justify-between text-sm" data-testid={`recap-item-${i}`}>
                <span><b>{it.quantity}×</b> {it.name}</span>
                <span className="text-[#4E5C4F]">${(it.price * it.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 pt-5 border-t border-[#1A6B2E]/10">
          <p className="font-label text-[10px] text-[#4E5C4F] mb-2">Your delivery notes</p>
          <p className="text-sm whitespace-pre-wrap bg-[#F4EFEB]/40 p-3 rounded-lg" data-testid="recap-notes">
            {order.notes}
          </p>
        </div>

        <div className="mt-5 pt-5 border-t border-[#1A6B2E]/10 space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-[#4E5C4F]">Subtotal</span><span>${order.subtotal.toFixed(2)}</span></div>
          {order.discount > 0 && <div className="flex justify-between text-[#1A6B2E]"><span>Discount ({order.promo_code})</span><span>− ${order.discount.toFixed(2)}</span></div>}
          <div className="flex justify-between"><span className="text-[#4E5C4F]">Delivery</span><span>{order.delivery_fee === 0 ? "FREE" : `$${order.delivery_fee.toFixed(2)}`}</span></div>
          {order.fresh_surcharge_amt > 0 && <div className="text-[10px] text-[#B57516] italic">Includes ${order.fresh_surcharge_amt.toFixed(2)} fresh-delivery surcharge (Zone 2).</div>}
          <div className="pt-2 mt-2 border-t border-[#1A6B2E]/10 flex justify-between items-baseline">
            <span className="font-label text-xs">Total due</span>
            <span data-testid="recap-total" className="font-display text-3xl">${order.total.toFixed(2)} CAD</span>
          </div>
        </div>
      </div>

      <div className="flex justify-center gap-3 pt-2">
        <Link to="/menu" className="rounded-full border border-[#1A6B2E]/25 px-6 py-3 text-sm hover:bg-[#F4EFEB]">Order more</Link>
        <button onClick={() => window.print()} className="rounded-full border border-[#1A6B2E]/25 px-6 py-3 text-sm hover:bg-[#F4EFEB]">Print / save</button>
      </div>
    </main>
  );
}

function Field({ label, value, onCopy, testid, mono, help }) {
  return (
    <div>
      <p className="font-label text-[10px] text-[#4E5C4F]">{label}</p>
      <div className="flex items-center justify-between gap-3 mt-1">
        <p data-testid={testid} className={`text-sm font-semibold text-[#0E2B13] break-all ${mono ? "font-mono" : ""}`}>{value}</p>
        <button
          type="button"
          onClick={onCopy}
          className="text-xs rounded-full border border-[#1A6B2E]/25 px-3 py-1 hover:bg-[#F4EFEB] inline-flex items-center gap-1 shrink-0"
        >
          <Copy className="w-3 h-3" /> Copy
        </button>
      </div>
      {help && <p className="text-[10px] text-[#4E5C4F] mt-1 italic">{help}</p>}
    </div>
  );
}
